"use client";

import * as React from "react";
import { cn } from "../../../lib/utils";
import { TrackingBoundary } from "../../../lib/sentry/index";
import { BackIcon, CloseIcon } from "../../wallet-info/components/icons";
import type { EstimateGasFn, GetTransactionReceiptFn, SendTransactionFn } from "../../wallet-portfolio/types";
import type { OnOutlink } from "../../../lib/outlink";
import type { Environment, Theme } from "../../../types";
import {
  useSendFlow,
  type SendFailedInfo,
  type SendSubmittedInfo,
} from "../model/useSendFlow";
import type { SendAccount, SendAsset } from "../types";
import { parseTokenAmount, isAddress } from "../utils";
import { SendAmount } from "./steps/SendAmount";
import { SendRecipient } from "./steps/SendRecipient";
import { SendResult } from "./steps/SendResult";
import { SendReview } from "./steps/SendReview";
import { SendTokenSelect } from "./steps/SendTokenSelect";
import "../styles/send-flow.css";

export interface SendFlowProps {
  env?: Environment;
  theme?: Theme;
  walletAddress: string;
  accountName?: string;
  accounts?: SendAccount[];
  /** First step shown when the flow opens. Defaults to `recipient`. */
  initialStep?: "token" | "recipient";
  token: SendAsset;
  tokens?: SendAsset[];
  onTokenChange?: (token: SendAsset) => void;
  sendTransaction?: SendTransactionFn;
  getTransactionReceipt?: GetTransactionReceiptFn;
  estimateGas?: EstimateGasFn;
  getTokenPriceUsd?: (token: SendAsset) => number | undefined;
  onSubmitted?: (info: SendSubmittedInfo) => void;
  onSuccess?: (txHash: `0x${string}`) => void;
  onFailed?: (info: SendFailedInfo) => void;
  /** Close the whole surface (X). */
  onClose?: () => void;
  /** Back out of the first step (return to the wallet view). */
  onBackToWallet?: () => void;
  /** Finished after a successful send. */
  onConfirmSuccess?: (txHash?: `0x${string}`) => void;
  onOutlink?: OnOutlink;
  className?: string;
}

const TITLES: Record<string, string> = {
  token: "Send",
  recipient: "Send",
  amount: "Send",
  review: "Sign Transaction",
  result: "",
};

export function SendFlow({
  onClose,
  onBackToWallet,
  onConfirmSuccess,
  onSuccess,
  className,
  ...rest
}: SendFlowProps) {
  const handleSuccess = React.useCallback(
    (txHash: `0x${string}`) => {
      onSuccess?.(txHash);
      onConfirmSuccess?.(txHash);
    },
    [onConfirmSuccess, onSuccess],
  );
  const successHandler = onSuccess || onConfirmSuccess ? handleSuccess : undefined;
  const flow = useSendFlow({
    ...rest,
    onSuccess: successHandler,
    onExit: onBackToWallet,
  });
  const { step, status } = flow;

  const recipientValid = isAddress(flow.recipient.trim());
  const amountParsed = parseTokenAmount(flow.amount, flow.token.quantity.decimals);
  const amountValid =
    "value" in amountParsed && amountParsed.value <= flow.balanceRaw;
  const sending = status === "submitting" || status === "confirming";

  const showBack = step !== "result";
  const title = TITLES[step] ?? "";

  return (
    <TrackingBoundary feature="send">
    <div className={cn("sf-root", className)} data-step={step}>
      <header className="sf-header">
        {showBack ? (
          <button
            type="button"
            className="sf-header-btn"
            onClick={flow.back}
            aria-label="Back"
            data-track="back"
          >
            <BackIcon size={24} />
          </button>
        ) : (
          <span className="sf-header-btn" aria-hidden />
        )}
        <span className="sf-header-title">{title}</span>
        <button
          type="button"
          className="sf-header-btn"
          onClick={onClose}
          aria-label="Close"
          data-track="close"
        >
          <CloseIcon size={24} />
        </button>
      </header>

      {step === "token" && <SendTokenSelect flow={flow} />}
      {step === "recipient" && <SendRecipient flow={flow} />}
      {step === "amount" && <SendAmount flow={flow} />}
      {step === "review" && <SendReview flow={flow} />}
      {step === "result" && <SendResult flow={flow} />}

      <footer className="sf-footer" data-step={step}>
        {step === "recipient" && (
          <button
            type="button"
            className="sf-btn sf-btn--primary"
            onClick={flow.submitRecipient}
            data-disabled={!recipientValid || undefined}
            aria-disabled={!recipientValid}
            data-track="recipient_next"
          >
            Next
          </button>
        )}
        {step === "amount" && (
          <button
            type="button"
            className="sf-btn sf-btn--primary"
            onClick={flow.submitAmount}
            data-disabled={!amountValid || undefined}
            aria-disabled={!amountValid}
            data-track="amount_next"
          >
            Next
          </button>
        )}
        {step === "review" && (
          <div className="sf-footer-row">
            <button
              type="button"
              className="sf-btn sf-btn--secondary"
              onClick={flow.back}
              disabled={sending}
              data-track="review_cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              className="sf-btn sf-btn--primary"
              onClick={flow.confirmSend}
              disabled={sending || !flow.canSend || flow.insufficientGas}
              data-track="review_send"
            >
              {sending ? "Sending..." : "Send"}
            </button>
          </div>
        )}
        {step === "result" && status === "success" && (
          <button
            type="button"
            className="sf-btn sf-btn--primary"
            onClick={() => onConfirmSuccess?.(flow.txHash || undefined)}
            data-track="result_ok"
          >
            All done
          </button>
        )}
        {step === "result" && status === "error" && (
          <button
            type="button"
            className="sf-btn sf-btn--primary"
            onClick={flow.retry}
            data-track="result_retry"
          >
            Try Again
          </button>
        )}
      </footer>
    </div>
    </TrackingBoundary>
  );
}
